<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>
    <template #content>
      <article class="blog-content" lang="es">
        <section class="intro">
          <p>
            La fotografía de interiores para tours virtuales 360° obedece a
            reglas diferentes a la fotografía inmobiliaria estándar. El objetivo
            no es una imagen plana perfectamente compuesta — es un panorama
            esférico sin costuras que representa fielmente el espacio desde un
            único punto de vista. Domina estas técnicas y tus tours virtuales
            tendrán sistemáticamente un acabado profesional.
          </p>
        </section>

        <section>
          <h2>Iluminación para fotografía de interiores 360°</h2>
          <h3>Luz natural — tu mejor aliada</h3>
          <p>
            Fotografía durante la parte más luminosa del día según la
            orientación de las ventanas de cada habitación. Abre completamente
            cortinas y persianas. La luz natural produce la representación más
            realista de cómo se siente un espacio. Evita fotografiar
            directamente hacia ventanas sin ocluir — si el exterior se quema,
            usa una lámina difusora o fotografía cuando el sol no esté
            directamente alineado con la ventana.
          </p>
          <h3>Iluminación artificial</h3>
          <p>
            Enciende todas las luces de techo, lámparas de pie y iluminación
            bajo muebles. Las temperaturas de color mixtas (cálidas vs frías)
            pueden crear problemas de dominante de color — si es posible,
            estandariza las temperaturas de las bombillas en toda la propiedad
            (todo cálido o todo luz de día). Los paneles LED o luces de rebote
            colocadas en las esquinas ayudan a rellenar las zonas de sombra sin
            ser visibles en el panorama.
          </p>
        </section>

        <section>
          <h2>
            Posicionamiento de la cámara para fotografía de interiores 360°
          </h2>
          <h3>Posición central</h3>
          <p>
            Colócate o sitúa la cámara en el centro geométrico de la habitación.
            Esto maximiza el espacio de suelo visible y asegura una cobertura
            uniforme en todas las direcciones. Un posicionamiento descentrado
            hace que una pared domine el panorama, reduciendo la sensación de
            espacio.
          </p>
          <h3>Altura</h3>
          <p>
            Captura a 1,2–1,5 m para interiores residenciales. Para espacios
            comerciales con techos altos, 1,5–1,7 m funciona mejor para
            equilibrar la perspectiva vertical. Evita capturar a nivel del suelo
            (todo parece sobredimensionado) o por encima de 1,8 m (los espacios
            parecen institucionales).
          </p>
        </section>

        <section>
          <h2>Consejos de postprocesado para fotos de interiores 360°</h2>
          <p>
            Tras la captura y el ensamblaje con IA: revisa las costuras en zonas
            de alto contraste (ventanas, luminarias) y corrígelas manualmente si
            es necesario. Ajusta la exposición global ligeramente más brillante
            de lo que crees necesario — los visores 360° tienden a mostrar los
            tours algo más oscuros que las fotos planas. Asegúrate de que la
            línea del horizonte esté perfectamente nivelada — cualquier
            inclinación es inmediatamente perceptible en un visor 360°.
          </p>
        </section>
        <section class="continue-reading">
          <h2>Seguir Leyendo</h2>
          <ul>
          <li><a href="/blog/es/guia-fotografia-360-inmobiliaria">Guía Completa de Fotografía 360° Inmobiliaria 2026</a></li>
          <li><a href="/blog/es/corregir-fotos-360-borrosas">Fotos 360° Borrosas: 6 Causas y Soluciones Concretas 2026</a></li>
          <li><a href="/blog/es/fotos-360-con-celular">Cómo Tomar Fotos 360° con tu Celular (Sin Cámara Especial) 2026</a></li>
          <li><a href="/blog/es/errores-fotografia-360">10 Errores de Fotografía 360° que Debes Evitar en 2026</a></li>
          <li><a href="/blog/es/tour-virtual-hotel-smartphone">Tour Virtual de Hotel con Smartphone: Aumenta tus Reservas</a></li>
          <li><a href="/blog/es/tour-virtual-360-guia-completa">Tour Virtual 360°: Guía Completa para Principiantes (2026)</a></li>
          </ul>
        </section>



        <RelatedPosts
          :currentSlug="postData.slug"
          :currentCategories="postData.categories"
        />
      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const postData = blogPostsData.find(
  (p) => p.slug === "fotografia-interior-360-tour-virtual",
);

useBlogSeo({
  title:
    "Fotografía de Interiores para Tours Virtuales 360°: Guía Técnica | Travvir",
  description:
    "Domina la fotografía de interiores para tours virtuales 360°. Iluminación, ángulos, posicionamiento y postprocesado para panoramas esféricos de calidad profesional.",
  keywords:
    "fotografía interior 360 tour virtual, foto interior 360, técnica foto 360 interior",
  slug: "fotografia-interior-360-tour-virtual",
  image: postData?.image,
  postData,
  lang: "es",
  faqItems: [
    {
      question: "¿Dónde posicionarse para fotografiar un interior en 360°?",
      answer:
        "En el centro geométrico de la habitación, a unos 1,2–1,5 m de altura. Esta posición maximiza el espacio visible y asegura una cobertura equilibrada en todas las direcciones.",
    },
    {
      question: "¿Cómo gestionar las costuras en fotos de interiores 360°?",
      answer:
        "Revisa las zonas de alto contraste (ventanas, luminarias) después del ensamblaje con IA. Ajusta la exposición ligeramente hacia arriba — los visores 360° tienden a mostrar las imágenes algo más oscuras de lo esperado.",
    },
  ],
});

</script>
